import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    Slider,
    FormControlLabel,
    Switch,
    Alert,
    Snackbar,
    Chip,
    LinearProgress,
    Collapse,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    PlayArrow,
    Stop,
    Save,
    Share,
    Delete,
    Refresh,
    ExpandMore,
    MusicNote,
    VolumeUp,
    Mic,
    Star,
    StarBorder,
    CloudUpload,
    InsertDriveFile,
} from '@mui/icons-material';
import { autoWebViewJs, autoJsStoreUtils } from '@droid/android';

// 扩展的紧急TTS设置接口
interface EmergencyTTSSettings {
    enabled: boolean;
    message: string;
    repeatInterval: number;
    voiceSpeed: number;
    voicePitch: number;
    lastUpdated: number;
    // 新增字段
    useCustomAudio: boolean;        // 是否使用自定义录音
    medicalEmergencyAudioPath?: string; // 医疗呼救默认音频路径
}

// 扩展的TTS文件信息接口
interface TTSFileInfo {
    fileName: string;
    filePath: string;
    fileSize: number;
    createTime: number;
    type: 'general' | 'medical' | 'security' | 'custom'; // 文件类型
    source: 'recorded' | 'uploaded';  // 新增来源标识
    isDefault?: boolean; // 是否为该类型的默认音频
    duration?: number;  // 音频时长（毫秒）
    format?: string;    // 文件格式
    mimeType?: string;  // MIME类型
}

// 文件上传相关接口
interface FileUploadResult {
    success: boolean;
    filePath: string;
    message: string;
    fileInfo?: TTSFileInfo;
}

interface SupportedFormats {
    formats: string[];
    maxFileSizeMB: number;
    maxDurationSeconds: number;
    recommendedDurationSeconds: number;
}

// 录制结果接口
interface TTSRecordResult {
    success: boolean;
    filePath: string;
    message: string;
}

// 语音呼救配置接口
interface VoiceEmergencyConfig {
    medicalDefault?: string;  // 医疗呼救默认音频路径
    securityDefault?: string; // 安全呼救默认音频路径
    lastUpdated: number;
}

// 默认设置
const DEFAULT_TTS_SETTINGS: EmergencyTTSSettings = {
    enabled: false,
    message: '请帮我从上衣右边口袋拿出降压药，喂我服下两颗，谢谢。我叫某某某，请帮我拨打我大儿子电话，13911119999，他叫某某。如果您是医生，我的病例可以通过我的蓝牙看到。谢谢您的帮助！',
    repeatInterval: 3000,
    voiceSpeed: 1.0,
    voicePitch: 1.0,
    lastUpdated: Date.now(),
    useCustomAudio: false,
};

const DEFAULT_VOICE_CONFIG: VoiceEmergencyConfig = {
    lastUpdated: Date.now(),
};

const TTS_SETTINGS_KEY = 'emergencyTTSSettings';
const VOICE_CONFIG_KEY = 'voiceEmergencyConfig';
const AUDIO_FILE_UPLOAD_SUCCESS_EVENT = 'audioFileUploadSuccess';

export interface VoiceEmergencySettingsRef {
    reloadConfig: () => Promise<void>;
}

const VoiceEmergencySettings = forwardRef<VoiceEmergencySettingsRef>((props, ref) => {
    const [settings, setSettings] = useState<EmergencyTTSSettings>(DEFAULT_TTS_SETTINGS);
    const [voiceConfig, setVoiceConfig] = useState<VoiceEmergencyConfig>(DEFAULT_VOICE_CONFIG);
    const [fileList, setFileList] = useState<TTSFileInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [playingFile, setPlayingFile] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [ttsAvailable, setTtsAvailable] = useState<boolean | null>(null);
    
    // 录制区域状态
    const [recordText, setRecordText] = useState(DEFAULT_TTS_SETTINGS.message);
    const [recordSpeed, setRecordSpeed] = useState(1.0);
    const [recordPitch, setRecordPitch] = useState(1.0);
    const [showRecordDialog, setShowRecordDialog] = useState(false);
    
    // 文件上传相关状态
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [supportedFormats, setSupportedFormats] = useState<SupportedFormats | null>(null);

    // 加载设置和配置
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            
            // 加载TTS设置
            const savedSettings = await autoJsStoreUtils.read<EmergencyTTSSettings>(
                TTS_SETTINGS_KEY, 
                DEFAULT_TTS_SETTINGS
            );
            setSettings(savedSettings);
            
            // 加载语音配置
            const savedConfig = await autoJsStoreUtils.read<VoiceEmergencyConfig>(
                VOICE_CONFIG_KEY,
                DEFAULT_VOICE_CONFIG
            );
            setVoiceConfig(savedConfig);
            
            // 检查TTS可用性
            checkTTSAvailability();
            
            // 加载文件列表
            loadFileList();
            
            // 加载支持的音频格式
            loadSupportedFormats();
        } catch (err) {
            console.error('加载数据失败:', err);
            setErrorMessage('加载设置失败');
            setShowError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const handleAudioUploadSuccess = (event: CustomEvent) => {
            console.log('收到音频上传成功事件:', event.detail);
            // 直接刷新文件列表（内联逻辑避免依赖循环）
            try {
                const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getFileList()`;
                const { javaResultString: listJson } = autoWebViewJs.callScript(script);
                const files = JSON.parse((listJson || "[]") as string) as TTSFileInfo[];
                const filesWithType = files.map(file => ({
                    ...file,
                    type: (file as any).type || 'general' as const,
                    isDefault: (file as any).isDefault || false,
                    source: (file as any).source || 'recorded' as const,
                }));
                setFileList(filesWithType);
            } catch (error) {
                console.error('刷新文件列表失败:', error);
            }
            // 显示成功消息
            setSuccessMessage(`音频文件上传成功: ${event.detail.fileName}`);
            setShowSuccess(true);
        };

        window.addEventListener(AUDIO_FILE_UPLOAD_SUCCESS_EVENT, handleAudioUploadSuccess as EventListener);

        loadData();

        // 清理函数
        return () => {
            window.removeEventListener(AUDIO_FILE_UPLOAD_SUCCESS_EVENT, handleAudioUploadSuccess as EventListener);
        };
    }, [loadData]);

    // 暴露重新加载方法给父组件
    useImperativeHandle(ref, () => ({
        reloadConfig: loadData,
    }), [loadData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // 加载支持的音频格式
    const loadSupportedFormats = useCallback(() => {
        try {
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getSupportedAudioFormats()`;
            const { javaResultString } = autoWebViewJs.callScript(script);
            const formats = JSON.parse(javaResultString || '{}') as SupportedFormats;
            setSupportedFormats(formats);
        } catch (error) {
            console.error('加载支持格式失败:', error);
            setSupportedFormats({
                formats: ['mp3', 'wav', 'm4a', 'aac'],
                maxFileSizeMB: 10,
                maxDurationSeconds: 60,
                recommendedDurationSeconds: 30
            });
        }
    }, []);

    // 选择并上传音频文件
    const handleSelectAndUploadAudioFile = useCallback(async () => {
        if (isUploading) return;
        
        try {
            setIsUploading(true);
            setUploadProgress(0);
            
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.selectAndUploadAudioFile()`;
            
            // 模拟上传进度
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 300);
            
            const { javaResultString } = autoWebViewJs.callScript(script);
            const result = JSON.parse(javaResultString || '{}') as FileUploadResult;
            
            clearInterval(progressInterval);
            setUploadProgress(100);
            
            if (result.success) {
                setSuccessMessage('音频文件上传成功！');
                setShowSuccess(true);
                // 刷新文件列表
                try {
                    const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getFileList()`;
                    const { javaResultString: listJson } = autoWebViewJs.callScript(script);
                    const files = JSON.parse((listJson || "[]") as string) as TTSFileInfo[];
                    const filesWithType = files.map(file => ({
                        ...file,
                        type: (file as any).type || 'general' as const,
                        isDefault: (file as any).isDefault || false,
                        source: (file as any).source || 'recorded' as const,
                    }));
                    setFileList(filesWithType);
                } catch (error) {
                    console.error('刷新文件列表失败:', error);
                }
            } else {
                setErrorMessage(result.message || '上传失败');
                setShowError(true);
            }
        } catch (error) {
            console.error('文件上传失败:', error);
            setErrorMessage('文件上传异常：' + (error as Error).message);
            setShowError(true);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [isUploading]);

    // 检查TTS可用性
    const checkTTSAvailability = useCallback(() => {
        try {
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.isTTSAvailable()`;
            const { javaResultString } = autoWebViewJs.callScript(script);
            setTtsAvailable(javaResultString === 'true');
        } catch (error) {
            console.error('检查TTS可用性失败:', error);
            setTtsAvailable(false);
        }
    }, []);

    // 加载文件列表
    const loadFileList = useCallback(() => {
        try {
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.getFileList()`;
            const { javaResultString: listJson } = autoWebViewJs.callScript(script);
            console.log('加载文件列表:', listJson);
            const files = JSON.parse((listJson || "[]") as string) as TTSFileInfo[];
            
            // 为现有文件设置默认类型（兼容性处理）
            const filesWithType = files.map(file => ({
                ...file,
                type: (file as any).type || 'general' as const,
                isDefault: (file as any).isDefault || false,
                source: (file as any).source || 'recorded' as const,
            }));
            
            setFileList(filesWithType);
        } catch (error) {
            console.error('加载文件列表失败:', error);
            setFileList([]);
        }
    }, []);

    // 保存TTS设置
    const saveTTSSettings = async (newSettings: EmergencyTTSSettings) => {
        try {
            const settingsToSave = {
                ...newSettings,
                lastUpdated: Date.now(),
            };
            
            await autoJsStoreUtils.write(TTS_SETTINGS_KEY, settingsToSave);
            setSettings(settingsToSave);
            setSuccessMessage('设置保存成功');
            setShowSuccess(true);
        } catch (err) {
            console.error('保存TTS设置失败:', err);
            setErrorMessage('保存设置失败');
            setShowError(true);
        }
    };

    // 保存语音配置
    const saveVoiceConfig = async (newConfig: VoiceEmergencyConfig) => {
        try {
            const configToSave = {
                ...newConfig,
                lastUpdated: Date.now(),
            };
            
            await autoJsStoreUtils.write(VOICE_CONFIG_KEY, configToSave);
            setVoiceConfig(configToSave);
        } catch (err) {
            console.error('保存语音配置失败:', err);
            setErrorMessage('保存配置失败');
            setShowError(true);
        }
    };

    // 设置为默认音频
    const setAsDefaultAudio = useCallback(async (filePath: string, type: 'medical' | 'security') => {
        try {
            const newConfig = { ...voiceConfig };
            if (type === 'medical') {
                newConfig.medicalDefault = filePath;
            } else {
                newConfig.securityDefault = filePath;
            }
            
            await saveVoiceConfig(newConfig);
            
            // 更新文件列表中的默认标记
            const updatedFiles = fileList.map(file => ({
                ...file,
                isDefault: file.filePath === filePath && file.type === type,
            }));
            setFileList(updatedFiles);
            
            setSuccessMessage(`已设置为${type === 'medical' ? '医疗' : '安全'}呼救默认音频`);
            setShowSuccess(true);
        } catch (error) {
            setErrorMessage('设置默认音频失败');
            setShowError(true);
        }
    }, [voiceConfig, fileList]);

    // 录制TTS
    const handleRecord = useCallback(() => {
        if (!recordText.trim()) {
            setErrorMessage('请输入要录制的文字');
            setShowError(true);
            return;
        }

        if (!ttsAvailable) {
            setErrorMessage('当前设备不支持TTS功能');
            setShowError(true);
            return;
        }

        setIsRecording(true);
        try {
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.recordTTS("${recordText.replace(/"/g, '\\"')}", null, ${recordSpeed}, ${recordPitch})`;
            const { javaResultString: resultJson } = autoWebViewJs.callScript(script);
            const result = JSON.parse(resultJson as string) as TTSRecordResult;
            
            setIsRecording(false);
            if (result.success) {
                setSuccessMessage(result.message);
                setShowSuccess(true);
                setShowRecordDialog(false);
                loadFileList();
            } else {
                setErrorMessage(result.message);
                setShowError(true);
            }
        } catch (error) {
            setIsRecording(false);
            setErrorMessage('录制失败：' + (error as Error).message);
            setShowError(true);
        }
    }, [recordText, recordSpeed, recordPitch, ttsAvailable, loadFileList]);

    // 播放文件
    const handlePlayFile = useCallback((filePath: string) => {
        try {
            const stopScript = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.stopPlayback()`;
            autoWebViewJs.callScript(stopScript);
            
            if (playingFile === filePath) {
                setPlayingFile(null);
                return;
            }

            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.playFile("${filePath.replace(/"/g, '\\"')}")`;
            const { javaResultString: success } = autoWebViewJs.callScript(script);
            if (success === 'true') {
                setPlayingFile(filePath);
                setTimeout(() => setPlayingFile(null), 3000);
            } else {
                setErrorMessage('播放失败');
                setShowError(true);
            }
        } catch (error) {
            setErrorMessage('播放异常：' + (error as Error).message);
            setShowError(true);
        }
    }, [playingFile]);

    // 分享文件
    const handleShareFile = useCallback((filePath: string) => {
        try {
            const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.shareFile("${filePath.replace(/"/g, '\\"')}", "紧急呼救语音")`;
            const { javaResultString: success } = autoWebViewJs.callScript(script);
            if (success !== 'true') {
                setErrorMessage('分享失败');
                setShowError(true);
            }
        } catch (error) {
            setErrorMessage('分享异常：' + (error as Error).message);
            setShowError(true);
        }
    }, []);

    // 删除文件
    const handleDeleteFile = useCallback((filePath: string) => {
        if (window.confirm('确定要删除这个文件吗？')) {
            try {
                const script = `com.fanfanlo.emergencycall.manager.TTSRecorderJSInterface.deleteFile("${filePath.replace(/"/g, '\\"')}")`;
                const { javaResultString: success } = autoWebViewJs.callScript(script);
                if (success === 'true') {
                    setSuccessMessage('文件删除成功');
                    setShowSuccess(true);
                    loadFileList();
                } else {
                    setErrorMessage('删除失败');
                    setShowError(true);
                }
            } catch (error) {
                setErrorMessage('删除异常：' + (error as Error).message);
                setShowError(true);
            }
        }
    }, [loadFileList]);

    // 设置变更处理
    const handleSettingChange = (key: keyof EmergencyTTSSettings, value: any) => {
        const newSettings = { ...settings, [key]: value };
        if (newSettings.enabled && !newSettings.useCustomAudio && !newSettings.message.trim()) {
            setErrorMessage('启用TTS功能时，呼救文字不能为空');
            setShowError(true);
            return;
        }
        setSettings(newSettings);
        saveTTSSettings(newSettings);
    };

    // 保存设置
    const handleSaveSettings = () => {
        if (settings.enabled && !settings.useCustomAudio && !settings.message.trim()) {
            setErrorMessage('启用TTS功能时，呼救文字不能为空');
            setShowError(true);
            return;
        }
        saveTTSSettings(settings);
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // 格式化时间
    const formatTime = (timestamp: number): string => {
        return new Date(timestamp).toLocaleString();
    };

    // 格式化时长
    const formatDuration = (milliseconds: number): string => {
        if (!milliseconds || milliseconds <= 0) return '';
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (minutes > 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return `${seconds}s`;
    };

    if (loading) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>加载中...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            {/* 折叠头部 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <VolumeUp /> */}
                        语音呼救
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.enabled}
                                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                                color="primary"
                            />
                        }
                        label={settings.enabled ? '已启用' : '已禁用'}
                    />
                    
                    {/* 状态指示器 */}
                    {/* <Chip 
                        size="small"
                        icon={ttsAvailable ? <VolumeUp /> : <MusicNote />}
                        label={ttsAvailable === null ? '检测中...' : (ttsAvailable ? 'TTS可用' : 'TTS不可用')}
                        color={ttsAvailable === null ? 'default' : (ttsAvailable ? 'success' : 'warning')}
                        variant="outlined"
                    /> */}
                </Box>
                
                <IconButton
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out'
                    }}
                >
                    <ExpandMore />
                </IconButton>
            </Box>

            {/* 折叠内容 */}
            <Collapse in={expanded}>
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    {/* 已有录音列表 */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                已有录音 ({fileList.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {/* <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Mic />}
                                    onClick={() => setShowRecordDialog(true)}
                                    disabled={!ttsAvailable}
                                >
                                    录制新语音
                                </Button> */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Refresh />}
                                    onClick={loadFileList}
                                >
                                    {/* 刷新列表 */}
                                </Button>
                            </Box>
                        </Box>

                        {fileList.length === 0 ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                }}
                            >
                                <Typography>暂无录音文件</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    点击"录制新语音"创建个性化呼救语音
                                </Typography>
                            </Paper>
                        ) : (
                            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                {fileList.map((file, index) => (
                                    <React.Fragment key={file.filePath}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ width: '100%' }}>
                                                        {/* 第一行：文件图标 + 文件名（允许多行） */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                            {file.source === 'uploaded' ? <InsertDriveFile fontSize="small" sx={{ mt: 0.2 }} /> : <MusicNote fontSize="small" sx={{ mt: 0.2 }} />}
                                                            <Typography 
                                                                variant="body1" 
                                                                sx={{ 
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    wordBreak: 'break-word',
                                                                    lineHeight: 1.4
                                                                }}
                                                            >
                                                                {file.fileName}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        {/* 第二行：状态标签 */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                            {playingFile === file.filePath && (
                                                                <Chip size="small" label="播放中" color="primary" />
                                                            )}
                                                            {file.isDefault && (
                                                                <Chip size="small" label="默认" color="success" icon={<Star />} />
                                                            )}
                                                            {/* <Chip 
                                                                size="small" 
                                                                label={file.source === 'uploaded' ? '上传' : '录制'} 
                                                                color={file.source === 'uploaded' ? 'secondary' : 'default'}
                                                                variant="outlined"
                                                            /> */}
                                                        </Box>
                                                        
                                                        {/* 第三行：操作按钮 */}
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handlePlayFile(file.filePath)}
                                                                color={playingFile === file.filePath ? "primary" : "default"}
                                                                title="播放/停止"
                                                                sx={{ 
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    '&:hover': { backgroundColor: 'action.hover' }
                                                                }}
                                                            >
                                                                {playingFile === file.filePath ? <Stop /> : <PlayArrow />}
                                                            </IconButton>
                                                            
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setAsDefaultAudio(file.filePath, 'medical')}
                                                                color={voiceConfig.medicalDefault === file.filePath ? "warning" : "default"}
                                                                title="设为医疗呼救默认"
                                                                sx={{ 
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    '&:hover': { backgroundColor: 'action.hover' }
                                                                }}
                                                            >
                                                                {voiceConfig.medicalDefault === file.filePath ? <Star /> : <StarBorder />}
                                                            </IconButton>
                                                            
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleShareFile(file.filePath)}
                                                                title="分享"
                                                                sx={{ 
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    '&:hover': { backgroundColor: 'action.hover' }
                                                                }}
                                                            >
                                                                <Share />
                                                            </IconButton>
                                                            
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteFile(file.filePath)}
                                                                color="error"
                                                                title="删除"
                                                                sx={{ 
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    '&:hover': { backgroundColor: 'action.hover' }
                                                                }}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            大小: {formatFileSize(file.fileSize)} | 创建: {formatTime(file.createTime)}
                                                            {file.duration && file.duration > 0 && ` | ${formatDuration(file.duration)}`}
                                                        </Typography>
                                                        {file.mimeType && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                {file.mimeType}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                sx={{ pr: 2 }}
                                            />
                                        </ListItem>
                                        {index < fileList.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />
                    <Box>
                        
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Mic />}
                                    onClick={() => setShowRecordDialog(true)}
                                    disabled={!ttsAvailable}
                                >
                                    文字转语音
                                </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* 上传音频文件区域 */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                            上传音频文件
                        </Typography>
                        
                        <Button
                            variant="outlined"
                            startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                            onClick={handleSelectAndUploadAudioFile}
                            disabled={isUploading}
                            sx={{ mb: 2 }}
                        >
                            {isUploading ? '上传中...' : '选择并上传音频文件'}
                        </Button>
                        
                        {/* 上传进度 */}
                        {isUploading && uploadProgress > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={uploadProgress} 
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                    上传中... {uploadProgress}%
                                </Typography>
                            </Box>
                        )}
                        
                        {supportedFormats && supportedFormats.formats && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                支持格式: {supportedFormats.formats.join(', ').toUpperCase()}<br />
                                文件大小限制: {supportedFormats.maxFileSizeMB}MB
                                {/* <br /> */}
                                {/* 推荐时长: {supportedFormats.recommendedDurationSeconds}秒内 */}
                            </Typography>
                        )}
                        
                        <Typography variant="body2" color="text.secondary">
                            • 支持从设备中选择现有的音频文件<br />
                            • 上传成功后可在文件管理中设置为默认音频<br />
                            • 上传的文件会显示"上传"标签以区分录制文件
                        </Typography>
                    </Box>

                    {/* 使用说明 */}
                    <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>使用说明:</strong><br />
                            • 可以录制个性化的呼救语音，或上传现有音频文件<br />
                            • 点击星标可以设置为医疗呼救的默认音频<br />
                            • 启用"优先使用录音文件"时，会优先播放设置的默认录音<br />
                            • 录制或上传的文件可以分享给亲友，在他们的设备上播放
                        </Typography>
                    </Box>
                </Paper>
            </Collapse>

            {/* 录制对话框 */}
            <Dialog open={showRecordDialog} onClose={() => setShowRecordDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>录制新语音</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={recordText}
                        onChange={(e) => setRecordText(e.target.value)}
                        placeholder="输入要转换为语音的文字..."
                        disabled={isRecording}
                        sx={{ mb: 2, mt: 1 }}
                        helperText={`当前字数: ${recordText.length} 字符`}
                    />

                    {/* 录制参数 */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            语音速度: {recordSpeed}x
                        </Typography>
                        <Slider
                            value={recordSpeed}
                            onChange={(_, value) => setRecordSpeed(value as number)}
                            min={0.5}
                            max={2.0}
                            step={0.1}
                            disabled={isRecording}
                            marks={[
                                { value: 0.5, label: '0.5x' },
                                { value: 1.0, label: '1.0x' },
                                { value: 2.0, label: '2.0x' },
                            ]}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="body2" sx={{ mb: 1 }}>
                            语音音调: {recordPitch}x
                        </Typography>
                        <Slider
                            value={recordPitch}
                            onChange={(_, value) => setRecordPitch(value as number)}
                            min={0.5}
                            max={2.0}
                            step={0.1}
                            disabled={isRecording}
                            marks={[
                                { value: 0.5, label: '低' },
                                { value: 1.0, label: '标准' },
                                { value: 2.0, label: '高' },
                            ]}
                        />
                    </Box>

                    {/* 录制进度 */}
                    {isRecording && (
                        <Box sx={{ mb: 2 }}>
                            <LinearProgress />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                正在录制中，请稍候...
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setShowRecordDialog(false)} 
                        disabled={isRecording}
                    >
                        取消
                    </Button>
                    <Button 
                        onClick={handleRecord} 
                        variant="contained"
                        disabled={!recordText.trim() || isRecording}
                    >
                        {isRecording ? '录制中...' : '开始录制'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 成功提示 */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={3000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowSuccess(false)} severity="success">
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* 错误提示 */}
            <Snackbar
                open={showError}
                autoHideDuration={6000}
                onClose={() => setShowError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowError(false)} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default VoiceEmergencySettings;